import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Sun, Mail, Building2, Calculator, Globe, Shield } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

/* About — "Over ons" page. Mounted at /over-ons. Minimal, honest framing:
   this calculator is offered for free by Recywatt Iberica S.L. */

const STR = {
  nl: {
    nav_calc: "Naar de calculator",
    badge: "Over Dr. Sol",
    h1_l1: "Eerlijke zonne-",
    h1_l2: "analyse voor Spanje.",
    intro: "Deze uitgebreide calculator wordt u gratis aangeboden door Recywatt Iberica S.L.",
    why_kicker: "Onze missie",
    why_h2: "Doorzichtigheid, eerst.",
    why_p: "De Spaanse zonne-markt is een doolhof van offertes, subsidies en technische keuzes. Dr. Sol vertaalt jouw situatie naar concrete cijfers — geen jargon, geen verkoopdruk, geen verborgen kosten.",
    blocks_kicker: "Wat we doen",
    b1_t: "Persoonlijke berekening",
    b1_p: "Op basis van je factuur of een paar snelle vragen berekenen we je besparing per maand, jaar en over 10 jaar.",
    b2_t: "Subsidies in kaart",
    b2_p: "IRPF, IBI, Next Generation EU-fondsen — wij zoeken uit welke subsidies voor jouw gemeente en provincie gelden.",
    b3_t: "Heel Spanje",
    b3_p: "Van Galicië tot Andalusië, van het binnenland tot de eilanden — onze berekening werkt voor elke provincie.",
    b4_t: "Vertrouwelijk",
    b4_p: "Jouw gegevens blijven van jou. We delen niets met derden zonder jouw expliciete toestemming.",
    legal_kicker: "Bedrijfsgegevens",
    legal_company: "Bedrijf",
    legal_company_v: "Recywatt Iberica S.L.",
    legal_contact: "Contact",
    cta_h2: "Klaar om te berekenen wat je verliest?",
    cta_btn: "Start de calculator →",
  },
  es: {
    nav_calc: "Ir a la calculadora",
    badge: "Sobre Dr. Sol",
    h1_l1: "Análisis solar honesto",
    h1_l2: "para España.",
    intro: "Esta calculadora completa te la ofrecemos gratis desde Recywatt Iberica S.L.",
    why_kicker: "Nuestra misión",
    why_h2: "Transparencia, primero.",
    why_p: "El mercado solar español es un laberinto de presupuestos, subvenciones y decisiones técnicas. Dr. Sol traduce tu situación a cifras concretas — sin jerga, sin presión comercial, sin costes ocultos.",
    blocks_kicker: "Qué hacemos",
    b1_t: "Cálculo personal",
    b1_p: "A partir de tu factura o de unas preguntas rápidas calculamos tu ahorro al mes, al año y en 10 años.",
    b2_t: "Subvenciones detalladas",
    b2_p: "IRPF, IBI, fondos Next Generation EU — averiguamos qué subvenciones aplican en tu municipio y provincia.",
    b3_t: "Toda España",
    b3_p: "De Galicia a Andalucía, del interior a las islas — nuestro cálculo funciona en cada provincia.",
    b4_t: "Confidencial",
    b4_p: "Tus datos son tuyos. No los compartimos con terceros sin tu permiso explícito.",
    legal_kicker: "Datos de la empresa",
    legal_company: "Empresa",
    legal_company_v: "Recywatt Iberica S.L.",
    legal_contact: "Contacto",
    cta_h2: "¿Listo para calcular lo que pierdes?",
    cta_btn: "Empezar la calculadora →",
  },
  en: {
    nav_calc: "Go to calculator",
    badge: "About Dr. Sol",
    h1_l1: "Honest solar analysis",
    h1_l2: "for Spain.",
    intro: "This extensive calculator is offered to you for free by Recywatt Iberica S.L.",
    why_kicker: "Our mission",
    why_h2: "Transparency, first.",
    why_p: "The Spanish solar market is a maze of quotes, subsidies and technical choices. Dr. Sol translates your situation into concrete numbers — no jargon, no sales pressure, no hidden costs.",
    blocks_kicker: "What we do",
    b1_t: "Personal calculation",
    b1_p: "From your bill or a handful of quick questions we calculate your savings per month, per year, and over 10 years.",
    b2_t: "Subsidies mapped out",
    b2_p: "IRPF, IBI, Next Generation EU funds — we figure out which subsidies apply in your municipality and province.",
    b3_t: "All of Spain",
    b3_p: "From Galicia to Andalusia, from inland to the islands — our calculation works for every province.",
    b4_t: "Confidential",
    b4_p: "Your data is yours. We don't share it with third parties without your explicit permission.",
    legal_kicker: "Company details",
    legal_company: "Company",
    legal_company_v: "Recywatt Iberica S.L.",
    legal_contact: "Contact",
    cta_h2: "Ready to calculate what you're losing?",
    cta_btn: "Start the calculator →",
  },
};
const t = (lang) => STR[(lang || "es").split("-")[0].toLowerCase()] || STR.es;

export default function About() {
  const { i18n } = useTranslation();
  const T = t(i18n.language);

  const blocks = [
    { icon: Calculator, title: T.b1_t, body: T.b1_p },
    { icon: Building2,  title: T.b2_t, body: T.b2_p },
    { icon: Globe,      title: T.b3_t, body: T.b3_p },
    { icon: Shield,     title: T.b4_t, body: T.b4_p },
  ];

  return (
    <div className="min-h-screen bg-[#F8F6F0] text-[#0F172A]"
         style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", WebkitFontSmoothing: "antialiased", letterSpacing: "-0.01em" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800&family=Inter:wght@400;500;600&display=swap');
        .h-display { font-family: 'Outfit', sans-serif; font-weight: 800; letter-spacing: -0.04em; line-height: 0.95; }
        .h-display-thin { font-family: 'Outfit', sans-serif; font-weight: 600; letter-spacing: -0.035em; }
      `}</style>

      <header className="px-5 md:px-12 lg:px-20 py-5 flex items-center justify-between border-b border-[#0F172A]/5">
        <Link to="/v4" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-9 h-9 rounded-full bg-[#E78938] flex items-center justify-center"><Sun className="w-5 h-5 text-white" /></div>
          <span className="h-display-thin text-xl">DoctorSol</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="minimal" />
          <Link to="/v4" data-testid="nav-calc"
            className="bg-[#0F172A] text-white px-5 py-2.5 rounded-full text-sm hover:bg-[#E78938] transition-colors">
            {T.nav_calc}
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="px-5 md:px-12 lg:px-20 pt-12 md:pt-20 pb-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-[#E78938]/10 text-[#E78938] px-3.5 py-1.5 rounded-full text-[12px] font-semibold border border-[#E78938]/20 mb-7">
            {T.badge}
          </div>
          <h1 className="h-display text-[44px] sm:text-[56px] lg:text-[72px] mb-6">
            {T.h1_l1}<br/>
            <span className="text-[#E78938]">{T.h1_l2}</span>
          </h1>
          <p className="text-xl text-[#0F172A] leading-relaxed max-w-xl">{T.intro}</p>
        </motion.div>
      </section>

      {/* MISSION */}
      <section className="bg-white border-y border-[#0F172A]/5 px-5 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="max-w-3xl mx-auto">
          <div className="text-sm uppercase tracking-widest text-[#E78938] font-bold mb-4">{T.why_kicker}</div>
          <h2 className="h-display text-3xl md:text-5xl tracking-tight leading-tight mb-6">{T.why_h2}</h2>
          <p className="text-lg text-[#475569] leading-relaxed">{T.why_p}</p>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="px-5 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="max-w-5xl mx-auto">
          <div className="text-sm uppercase tracking-widest text-[#E78938] font-bold mb-10">{T.blocks_kicker}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {blocks.map((b) => (
              <div key={b.title} className="bg-white border border-[#0F172A]/5 rounded-2xl p-7 hover:shadow-[0_8px_32px_rgba(15,23,42,0.06)] transition-shadow">
                <b.icon className="w-6 h-6 text-[#E78938] mb-4" />
                <h3 className="h-display-thin text-lg font-semibold mb-2">{b.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEGAL / COMPANY */}
      <section className="bg-[#0F172A] text-white px-5 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="max-w-3xl mx-auto">
          <div className="text-sm uppercase tracking-widest text-[#E78938] font-bold mb-4">{T.legal_kicker}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 mt-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">{T.legal_company}</div>
              <div className="h-display-thin text-2xl">{T.legal_company_v}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">{T.legal_contact}</div>
              <a href="mailto:info@doctorsol.es" className="h-display-thin text-2xl text-[#E78938] hover:underline inline-flex items-center gap-2">
                <Mail className="w-5 h-5" /> info@doctorsol.es
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 md:px-12 lg:px-20 py-24 md:py-32 text-center">
        <h2 className="h-display text-3xl md:text-5xl mb-8 max-w-2xl mx-auto">{T.cta_h2}</h2>
        <Link to="/v4" data-testid="about-cta"
          className="inline-flex items-center gap-2 bg-[#E78938] text-white rounded-full px-10 py-5 text-lg font-medium hover:bg-[#D17627] hover:shadow-[0_8px_32px_rgba(231,137,56,0.3)] transition-all">
          {T.cta_btn}
        </Link>
      </section>

      <footer className="border-t border-[#0F172A]/10 px-5 md:px-12 lg:px-20 py-8 text-xs text-[#94A3B8] flex flex-wrap justify-between gap-3">
        <div>© 2026 Recywatt Iberica S.L. · DoctorSol</div>
        <div className="flex gap-5">
          <a href="/privacy">Privacy</a>
          <a href="/voorwaarden">Voorwaarden</a>
          <a href="mailto:info@doctorsol.es">Contact</a>
        </div>
      </footer>
    </div>
  );
}
